import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { titleCase } from '../lib/format';

/* --- Panel ---------------------------------------------------------------- */

export function Panel({
  title,
  hint,
  action,
  children,
  raised = true,
  className = '',
  collapsibleKey,
  defaultCollapsed = false,
}: {
  title?: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  raised?: boolean;
  className?: string;
  /**
   * Makes the panel collapsible, and the key its remembered state is stored
   * under. Passing it is what opts a panel in — most panels should not be
   * collapsible, because a page of collapsed headers is a page that has hidden
   * itself.
   *
   * Stored in `localStorage` rather than on the profile: it is a per-device
   * view preference, not something about the player. Somebody who folds the
   * task board on their phone has said nothing about their desktop.
   */
  collapsibleKey?: string;
  defaultCollapsed?: boolean;
}) {
  const storageKey = collapsibleKey ? `pokegrove.panel.${collapsibleKey}` : null;

  const [collapsed, setCollapsed] = useState(() => {
    if (!storageKey) return false;
    try {
      const saved = window.localStorage.getItem(storageKey);
      return saved === null ? defaultCollapsed : saved === '1';
    } catch {
      // Private mode, or storage disabled. A panel that will not remember is
      // still a panel that folds.
      return defaultCollapsed;
    }
  });

  function toggle() {
    setCollapsed((current) => {
      const next = !current;
      try {
        if (storageKey) window.localStorage.setItem(storageKey, next ? '1' : '0');
      } catch {
        /* not remembering is survivable; not folding is not */
      }
      return next;
    });
  }

  const head = (title || action) && (
    <header className="panel__head">
      <h2 className="panel__title">{title}</h2>
      {action ?? (hint ? <span className="panel__hint">{hint}</span> : null)}
    </header>
  );

  if (!storageKey) {
    return (
      <section className={`pxframe ${raised ? 'pxframe--raised' : ''} panel ${className}`}>
        {head}
        {children}
      </section>
    );
  }

  // The whole header is the control, so the target is a full row rather than a
  // chevron — this is most often reached on a phone, where the panel being too
  // tall is the problem in the first place. `hint` stays visible while
  // collapsed on purpose: "Daily tasks 3 / 7" folded is still useful, whereas a
  // bare title is just a thing to reopen.
  return (
    <section
      className={`pxframe ${raised ? 'pxframe--raised' : ''} panel panel--collapsible ${
        collapsed ? 'panel--collapsed' : ''
      } ${className}`}
    >
      <button
        type="button"
        className="panel__head panel__toggle"
        onClick={toggle}
        aria-expanded={!collapsed}
        // `title` can be a whole element, so the accessible name is stated
        // rather than inferred from whatever happens to be inside the header.
        aria-label={typeof title === 'string' ? title : collapsibleKey}
      >
        <span className="panel__chevron" aria-hidden>
          {collapsed ? '▸' : '▾'}
        </span>
        <h2 className="panel__title">{title}</h2>
        {action ?? (hint ? <span className="panel__hint">{hint}</span> : null)}
        <span className="sr-only">{collapsed ? 'Expand' : 'Collapse'}</span>
      </button>
      {!collapsed && children}
    </section>
  );
}

/* --- Button --------------------------------------------------------------- */

type Variant = 'default' | 'primary' | 'go' | 'info' | 'gold' | 'special' | 'ghost';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'sm' | 'md' | 'lg';
  block?: boolean;
  loading?: boolean;
}

export function Button({
  variant = 'default',
  size = 'md',
  block,
  loading,
  disabled,
  children,
  className = '',
  ...rest
}: ButtonProps) {
  const classes = [
    'btn',
    variant !== 'default' ? `btn--${variant}` : '',
    size !== 'md' ? `btn--${size}` : '',
    block ? 'btn--block' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button className={classes} disabled={disabled || loading} {...rest}>
      {loading ? <Spinner /> : children}
    </button>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: 12,
        height: 12,
        background: 'currentColor',
        animation: 'spin-step 0.8s steps(8) infinite',
      }}
    />
  );
}

/* --- Chips ---------------------------------------------------------------- */

export function TypeChip({ type }: { type: string }) {
  return (
    <span className="chip chip--type" style={{ background: `var(--type-${type}, var(--ink-faint))` }}>
      {titleCase(type)}
    </span>
  );
}

export function ShinyChip() {
  return <span className="chip chip--shiny">✦ Shiny</span>;
}

/* --- Progress ------------------------------------------------------------- */

export function Bar({
  ratio,
  label,
  variant = 'default',
  tall,
}: {
  ratio: number;
  label?: ReactNode;
  variant?: 'default' | 'exp' | 'egg' | 'ready';
  tall?: boolean;
}) {
  const clamped = Math.max(0, Math.min(1, ratio));
  return (
    <div
      className={`bar ${variant !== 'default' ? `bar--${variant}` : ''} ${tall ? 'bar--tall' : ''}`}
      role="progressbar"
      aria-valuenow={Math.round(clamped * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="bar__fill" style={{ width: `${clamped * 100}%` }} />
      {label && <span className="bar__label">{label}</span>}
    </div>
  );
}

/* --- States --------------------------------------------------------------- */

export function EmptyState({
  art = '○',
  title,
  children,
  action,
}: {
  art?: string;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty__art" aria-hidden>
        {art}
      </div>
      <h3>{title}</h3>
      {children && <p className="tiny muted" style={{ maxWidth: 340 }}>{children}</p>}
      {action}
    </div>
  );
}

export function LoadingGrid({ count = 8, height = 150 }: { count?: number; height?: number }) {
  return (
    <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="pxwell skeleton" style={{ height }} />
      ))}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <EmptyState
      art="✖"
      title="That didn't load"
      action={onRetry ? <Button onClick={onRetry}>Try again</Button> : undefined}
    >
      {message}
    </EmptyState>
  );
}

/* --- Modal ---------------------------------------------------------------- */

export function Modal({
  onClose,
  children,
  labelledBy,
  wide,
  size,
  className = '',
}: {
  onClose: () => void;
  children: ReactNode;
  labelledBy?: string;
  /** Shorthand for `size="wide"`, kept because several pages already pass it. */
  wide?: boolean;
  /**
   * How much room the dialog gets. Most modals are a confirmation and want the
   * default; an authoring form is a working surface and needs the width, and
   * squeezing one into a confirmation-sized box is what makes it feel cramped
   * rather than what makes it focused.
   */
  size?: 'default' | 'wide' | 'work';
  /**
   * Lets a caller own the *inside* layout — a modal whose body scrolls while
   * its header and footer stay put cannot be built from the outside.
   */
  className?: string;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Stop the page behind from scrolling while a modal is open.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
    >
      <div
        className={`pxframe pxframe--raised modal modal--${size ?? (wide ? 'wide' : 'default')} ${className}`}
        onClick={(event) => event.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

/* --- Tabs ----------------------------------------------------------------- */

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: Array<{ value: T; label: string; badge?: number }>;
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          role="tab"
          type="button"
          aria-selected={tab.value === value}
          className="tab"
          onClick={() => onChange(tab.value)}
        >
          {tab.label}
          {tab.badge !== undefined && tab.badge > 0 && (
            <span style={{ marginLeft: 6, color: 'var(--berry-dark)' }}>{tab.badge}</span>
          )}
        </button>
      ))}
    </div>
  );
}

/* --- Currency ------------------------------------------------------------- */

export function Cost({ amount, currency }: { amount: number; currency: string }) {
  const isCoins = currency === 'pokeCoins';
  return (
    <span className="row" style={{ gap: 5 }}>
      <span className={`coin__dot ${isCoins ? 'coin__dot--gold' : 'coin__dot--token'}`} aria-hidden />
      <span className="mono">{amount.toLocaleString()}</span>
      <span className="sr-only">{isCoins ? 'PokeCoins' : 'Friendship Tokens'}</span>
    </span>
  );
}

/* --- Confetti ------------------------------------------------------------- */

const CONFETTI_COLORS = ['var(--gold)', 'var(--berry)', 'var(--leaf)', 'var(--sky)', 'var(--plum)'];

/** Purely decorative burst; positioned by the parent's `position: relative`. */
export function Confetti({ count = 18 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => {
        const angle = (index / count) * Math.PI * 2;
        const distance = 90 + (index % 4) * 34;
        return (
          <span
            key={index}
            className="confetti"
            aria-hidden
            style={
              {
                background: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
                animationDelay: `${(index % 5) * 40}ms`,
                '--dx': `${Math.cos(angle) * distance}px`,
                '--dy': `${Math.sin(angle) * distance + 40}px`,
              } as React.CSSProperties
            }
          />
        );
      })}
    </>
  );
}
